type PostHog = (typeof import("posthog-js"))["default"];

/**
 * The campaign tags of the address a visitor landed on, carried across
 * the consent wait.
 *
 * PostHog reads the tags off the address as it is at its first capture.
 * Where analytics waits for a yes, a visitor who moves on first has lost
 * them by then, and the visit counts as direct. So the tags are read as
 * the page loads and held in memory only: nothing is written to the
 * browser and nothing is sent before the yes, a no forgets them, and a
 * reload loses them. An ad's click id is never carried.
 */

/** The tags carried: an ad's own words for itself, never an id of the click. */
const CAMPAIGN_TAGS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

/** Where PostHog keeps the first address and referrer it saw for this browser. */
const INITIAL_PERSON_INFO = "$initial_person_info";
/** Where PostHog keeps the address and referrer a session began on. */
const CLIENT_SESSION_PROPS = "$client_session_props";

/** PostHog's own name for a visit with no referrer. */
const DIRECT = "$direct";

/** The landing address with its campaign tags and nothing else of its query. */
export interface Landing {
    url: string;
    referrer: string;
}

/** The tags an address carries, in order, the empty ones left out. */
function tagsOf(href: string): URLSearchParams {
    const tags = new URLSearchParams();
    const params = new URL(href).searchParams;
    for (const name of CAMPAIGN_TAGS) {
        const value = params.get(name);
        if (value) tags.set(name, value);
    }
    return tags;
}

/** Whether an address carries a campaign tag of its own. */
export function hasCampaignTags(href: string): boolean {
    return tagsOf(href).size > 0;
}

/** The landing address's tags and referrer, or null where it carried no tag. */
export function readLanding(href: string, referrer: string): Landing | null {
    const tags = tagsOf(href);
    if (tags.size === 0) return null;
    const { origin, pathname } = new URL(href);
    return { url: `${origin}${pathname}?${tags}`, referrer: referrer || DIRECT };
}

/**
 * Tells PostHog, just started and before its first capture, the address
 * the visitor landed on: the tags go on this session's events, the first
 * address of a browser seen for the first time, and the session's entry
 * when the session began just now. Nothing where the address still has
 * tags of its own, which PostHog reads itself.
 *
 * The two records PostHog keeps an address in have no setter of their
 * own; `campaign.carry.test.ts` runs the real SDK, so a rename fails there.
 */
export function carryLanding(posthog: PostHog, landing: Landing, href: string): void {
    if (hasCampaignTags(href)) return;
    posthog.register_for_session(Object.fromEntries(tagsOf(landing.url)));
    const info = { r: landing.referrer, u: landing.url };
    // PostHog may have recorded the first visit as this page already: its
    // own `$set` at start does, on a test host. A browser seen before keeps
    // the first visit it had.
    const first = posthog.get_property(INITIAL_PERSON_INFO) as PersonInfo | undefined;
    if (!first || first.u === href) posthog.register({ [INITIAL_PERSON_INFO]: info });
    // Starts the session if none is running, which records its entry as
    // the address now. A session begun on an earlier page keeps its own.
    const sessionId = posthog.get_session_id();
    const entry = posthog.get_property(CLIENT_SESSION_PROPS) as
        { sessionId?: string; props?: PersonInfo } | undefined;
    if (entry?.sessionId === sessionId && entry.props?.u === href)
        posthog.register({ [CLIENT_SESSION_PROPS]: { sessionId, props: info } });
}

/** An address and referrer as PostHog keeps them. */
interface PersonInfo {
    r?: string;
    u?: string;
}
